/**
 * Listing Data Helpers
 *
 * Utility functions for extracting and transforming listing page data
 */

import type { CMSContentItem, CMSHeroProductListing } from '@/lib/cms';
import { replaceSeoVariables, type SeoVariables } from '@/lib/seo';
import type { BreadcrumbItem, HeroMedia, HeroSettings, SEOConfig } from './types';

// ============================================
// Type Guards
// ============================================

/**
 * Type guard to check if a CMS item is a HeroProductListing
 */
export function isHeroProductListing(item: CMSContentItem): item is CMSHeroProductListing {
  return item.__typename === 'HeroProductListing';
}

// ============================================
// Hero Settings Extraction
// ============================================

/**
 * Extract hero settings from an ADAPTED HeroProductListing CMS entry
 *
 * The adapters (lib/cms/adapters/) transform raw Contentful data into
 * domain types with consistent field names.
 *
 * @param heroItem - Adapted HeroProductListing from CMS adapter
 * @returns Normalized hero settings
 */
export function extractHeroSettings(heroItem: CMSHeroProductListing): HeroSettings {
  // The adapters normalize the field names:
  // - heroHeading (GraphQL alias) → heading (domain type)
  // - heroSubheader/heroSubheading → subheader/subheading
  // - media is adapted to CMSMedia with url, title, alt, contentType, focalPoint

  // Extract media - adapters transform to CMSMedia format
  let media: HeroMedia | null = null;
  if (heroItem.media?.url) {
    const contentType = heroItem.media.contentType || '';
    const isVideo = contentType.startsWith('video/');
    media = {
      url: heroItem.media.url,
      alt: heroItem.media.alt || heroItem.media.title || undefined,
      type: isVideo ? 'video' : 'image',
      focalPoint: heroItem.media.focalPoint || undefined,
    };
  }

  return {
    // Adapted fields use domain type names
    heading: heroItem.heading || null,
    subheading: heroItem.subheader || heroItem.subheading || null,
    textAlignment: heroItem.textAlignment || 'center',
    headingNumberOfColoredWords: heroItem.headingNumberOfColoredWords || null,
    headingSeoTag: heroItem.headingSeoTag || 'h1',
    headingStyle: heroItem.headingStyle || null,
    subheaderIsCapitalized: heroItem.subheaderIsCapitalized === true,
    media,
    backgroundColor: heroItem.backgroundColor || null,
    backgroundColorOpacity: heroItem.backgroundColorOpacity ?? null,
    textColor: heroItem.textColor || null,
    textBelowMedia: heroItem.textBelowMedia ?? false,
    showFilters: heroItem.showFilters ?? true, // Default to showing filters
    showBreadcrumbs: heroItem.showBreadcrumbs ?? true, // Default to showing breadcrumbs
  };
}

/**
 * Find and extract HeroProductListing from content array
 * Returns the hero settings and the remaining content without the hero
 *
 * @param content - Array of CMS content items (adapted by lib/cms/adapters)
 * @returns Object containing heroSettings and filtered content
 */
export function extractHeroFromContent(content: CMSContentItem[]): {
  heroSettings: HeroSettings | null;
  content: CMSContentItem[];
} {
  // Find HeroProductListing in the adapted content
  const heroIndex = content.findIndex(isHeroProductListing);

  if (heroIndex === -1) {
    return { heroSettings: null, content };
  }

  // Type guard ensures this is CMSHeroProductListing
  const heroItem = content[heroIndex];
  if (!isHeroProductListing(heroItem)) {
    return { heroSettings: null, content };
  }

  const heroSettings = extractHeroSettings(heroItem);

  // Remove HeroProductListing from content array (it's handled separately)
  const filteredContent = [...content.slice(0, heroIndex), ...content.slice(heroIndex + 1)];

  return { heroSettings, content: filteredContent };
}

// ============================================
// SEO Configuration Extraction
// ============================================

/**
 * Extract SEO configuration from listing page data
 *
 * Optionally replaces %%VARIABLE%% placeholders in title and description.
 *
 * @param listingPage - Listing page data from Contentful
 * @param seoVariables - Optional variables for placeholder replacement
 */
export function extractSEOConfig(
  listingPage: {
    seoConfiguration?: {
      seoTitle?: string | null;
      seoDescription?: string | null;
      seoThumbnail?: { url: string } | null;
    } | null;
  } | null,
  seoVariables?: SeoVariables
): SEOConfig | null {
  const seo = listingPage?.seoConfiguration;
  if (!seo) return null;

  // Apply SEO variable replacement if variables provided
  const seoTitle =
    seoVariables && seo.seoTitle
      ? replaceSeoVariables(seo.seoTitle, seoVariables)
      : seo.seoTitle || null;

  const seoDescription =
    seoVariables && seo.seoDescription
      ? replaceSeoVariables(seo.seoDescription, seoVariables)
      : seo.seoDescription || null;

  return {
    seoTitle,
    seoDescription,
    seoThumbnail: seo.seoThumbnail || null,
  };
}

// Re-export SeoVariables for convenience
export type { SeoVariables };

// ============================================
// Breadcrumb Generation
// ============================================

/**
 * Strip HTML tags from a string
 * Used for breadcrumbs where heading may contain <br> tags
 */
function stripHtmlTags(str: string): string {
  // Replace <br>, <br/>, <br /> with space, then remove other HTML tags
  return str
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Generate breadcrumb items for a listing page.
 *
 * Returns locale-agnostic paths for next-intl Link (locale prefix is added automatically),
 * matching generatePDPBreadcrumbs.
 *
 * The self-referential-crumb drop and the collection-name override (BEC-24794) are Gallet-shaped
 * only: this function is brand-agnostic and these PLP routes are shared with Breitling, so
 * `options.isGalletShaped` must be threaded in explicitly rather than read from brand config here.
 * Off the Gallet-shaped path this reproduces the pre-BEC-24794 output byte-for-byte, so Breitling
 * is unaffected (AC8).
 *
 * @param type - Listing page type (config.cmsType)
 * @param heading - Page heading (for current page label)
 * @param translations - Translated labels
 * @param options.collectionName - Formatted collection name (formatCollectionName(slug)), used as
 *   the current-page label on Gallet-shaped `watch collection` pages instead of the generic
 *   heading/title. Ignored when `isGalletShaped` is false.
 * @param options.isGalletShaped - Gates the BEC-24794 behaviour to Gallet-shaped brands (Gallet + UG).
 */
export function generateBreadcrumbs(
  type: string,
  heading: string | null,
  translations: {
    home: string;
    collections?: string;
    watches?: string;
    straps?: string;
    all?: string;
  },
  options: { collectionName?: string | null; isGalletShaped: boolean }
): BreadcrumbItem[] {
  const { collectionName, isGalletShaped } = options;
  const items: BreadcrumbItem[] = [{ label: translations.home, href: '/' }];

  // "All the *" listing types have no separate index route to link to (there is
  // no standalone /watches or /straps page), so the intermediate crumb used to
  // point at the page the visitor is already on. Gallet-shaped brands drop it:
  // the section label itself becomes the current, unlinked item instead.
  // Breitling keeps the pre-fix, self-referential intermediate (AC8) — see the
  // `.includes(...)` fallback below.
  if (isGalletShaped) {
    if (type === 'all the watches') {
      items.push({ label: translations.watches || 'Watches' });
      return items;
    }
    if (type === 'all the straps') {
      items.push({ label: translations.straps || 'Straps' });
      return items;
    }
  }

  // Add intermediate breadcrumb based on page type. On the Gallet-shaped path
  // these types link somewhere genuinely different from the current page, so
  // the intermediate stays. Off the Gallet-shaped path this matches pre-fix
  // matching exactly (`.includes(...)`), self-referential "all the *" crumb
  // included, so Breitling's output does not change (AC8).
  if (type === 'watch collection') {
    // Collection pages go under Collections, not Watches
    items.push({
      label: translations.collections || 'Collections',
      href: '/collections',
    });
  } else if (isGalletShaped ? type === 'watch editorial page' : type.includes('watch')) {
    // No /watches index — the watches listing lives at /watches/all
    items.push({
      label: translations.watches || 'Watches',
      href: '/watches/all',
    });
  } else if (isGalletShaped ? type === 'strap material' : type.includes('strap')) {
    items.push({
      label: translations.straps || 'Straps',
      href: '/straps',
    });
  }

  // Add current page (no href = current page)
  // Strip HTML tags from heading (e.g., <br> tags) for clean breadcrumb text
  const cleanHeading = heading ? stripHtmlTags(heading) : null;
  const currentLabel =
    isGalletShaped && type === 'watch collection' && collectionName
      ? collectionName
      : cleanHeading || getDefaultLabelForType(type, translations);
  items.push({ label: currentLabel });

  return items;
}

/**
 * Get default label for page type when no heading is provided
 */
function getDefaultLabelForType(type: string, translations: { all?: string }): string {
  switch (type) {
    case 'all the watches':
    case 'all the straps':
      return translations.all || 'All';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
}

// ============================================
// Algolia Settings Extraction
// ============================================

/**
 * Extract Algolia-related settings from listing page
 */
export function extractAlgoliaSettings(
  listingPage: {
    algoliaRuleName?: string | null;
    algoliaCategoryId?: string | null;
  } | null
): {
  algoliaRuleName: string | null;
  algoliaCategoryId: string | null;
} {
  return {
    algoliaRuleName: listingPage?.algoliaRuleName || null,
    algoliaCategoryId: listingPage?.algoliaCategoryId || null,
  };
}
